import { useState, useEffect } from 'react';
import { X, Loader, Check, Shield, Search, Database } from 'lucide-react';
import type { User } from '../types/user';
import type { SavedQuery } from '../types/query';
import { queryService } from '../services/queryService';
import { permissionService } from '../services/permissionService';

interface UserPermissionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  onSave: () => void;
}

export default function UserPermissionsModal({ isOpen, onClose, user, onSave }: UserPermissionsModalProps) {
  const [services, setServices] = useState<SavedQuery[]>([]);
  const [selectedServices, setSelectedServices] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen && user) {
      loadData();
    }
  }, [isOpen, user]);

  const loadData = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      // Carregar todos os serviços
      const allServices = await queryService.getQueries();
      setServices(allServices);

      // Carregar permissões do usuário
      const userPermissions = await permissionService.getUserPermissions(user.guid);
      setSelectedServices(new Set(userPermissions));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleService = (serviceId: string) => {
    const newSelected = new Set(selectedServices);
    if (newSelected.has(serviceId)) {
      newSelected.delete(serviceId);
    } else {
      newSelected.add(serviceId);
    }
    setSelectedServices(newSelected);
  };

  const handleSelectAll = () => {
    const filtered = getFilteredServices();
    if (selectedServices.size === filtered.length) {
      // Desmarcar todos os filtrados
      const newSelected = new Set(selectedServices);
      filtered.forEach(service => newSelected.delete(service.id));
      setSelectedServices(newSelected);
    } else {
      // Marcar todos os filtrados
      const newSelected = new Set(selectedServices);
      filtered.forEach(service => newSelected.add(service.id));
      setSelectedServices(newSelected);
    }
  };

  const handleSave = async () => {
    if (!user) return;

    try {
      setSaving(true);
      setError(null);
      await permissionService.setUserPermissions(user.guid, Array.from(selectedServices));
      onSave();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar permissões');
    } finally {
      setSaving(false);
    }
  };

  const getFilteredServices = () => {
    return services.filter(service =>
      service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      service.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      service.endpoint_slug.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  if (!isOpen || !user) return null;

  const filteredServices = getFilteredServices();
  const allFilteredSelected = filteredServices.length > 0 &&
    filteredServices.every(service => selectedServices.has(service.id));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Permissões de Acesso</h2>
              <p className="text-sm text-gray-500">{user.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
            <X className="w-4 h-4 text-red-600" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Buscar serviços..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
            <button
              onClick={handleSelectAll}
              disabled={loading || filteredServices.length === 0}
              className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {allFilteredSelected ? 'Desmarcar Todos' : 'Marcar Todos'}
            </button>
          </div>
          <div className="mt-2 text-xs text-gray-500">
            {selectedServices.size} de {services.length} serviços selecionados
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader className="w-8 h-8 animate-spin text-blue-600 mb-4" />
              <p className="text-gray-600">Carregando serviços...</p>
            </div>
          ) : filteredServices.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Database className="w-12 h-12 text-gray-300 mb-4" />
              <p className="text-gray-600">
                {searchTerm ? 'Nenhum serviço encontrado' : 'Nenhum serviço cadastrado'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredServices.map((service) => {
                const isSelected = selectedServices.has(service.id);
                return (
                  <button
                    key={service.id}
                    onClick={() => handleToggleService(service.id)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 transition-colors ${
                        isSelected
                          ? 'border-blue-500 bg-blue-500'
                          : 'border-gray-300 bg-white'
                      }`}>
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h4 className="font-medium text-gray-900">{service.name}</h4>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            service.is_active
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {service.is_active ? 'Ativo' : 'Inativo'}
                          </span>
                        </div>
                        {service.description && (
                          <p className="text-sm text-gray-600 mb-2">{service.description}</p>
                        )}
                        <code className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                          {service.endpoint_slug}
                        </code>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            {selectedServices.size} {selectedServices.size === 1 ? 'serviço selecionado' : 'serviços selecionados'}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-colors flex items-center gap-2"
            >
              {saving && <Loader className="w-4 h-4 animate-spin" />}
              Salvar Permissões
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
